import { Box, BoxProps, ToggleTip } from "@compono/ui";
import React, { ReactNode, useRef, useState } from "react";
import LineMarker from "../PlotGraph/markers/LineMarker";
import { calculateMarkerOffset, calculateWidth } from "./utils";
export const color1 = "#FF2E2E";
export const color2 = "#F97316";
export const color3 = "#FFE29B";
export const color4 = "#93E0D2";
export const color5 = "#14B8A6";

export type BarItem = {
  value: number;
  color: string;
  content?: ReactNode;
};

type BarStackChartProps = {
  width?: number;
  height?: number;
  items: BarItem[];
  cardBottomView?: boolean;
  markerValue?: number;
};

type HoverItemProps = {
  width: string;
  sx: BoxProps["sx"];
  tooltipContent?: ReactNode;
};

const HoverItem = ({ width, sx, tooltipContent }: HoverItemProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  return (
    <Box
      sx={{
        ...sx,
        width,
      }}
      onMouseOver={() => {
        setIsOpen(true);
      }}
      onMouseOut={() => {
        setIsOpen(false);
      }}
    >
      <ToggleTip
        label="Default application"
        isOpen={isOpen}
        onExpanded={() => {
          setIsOpen(true);
        }}
        icon={<></>}
        buttonRef={buttonRef}
      >
        {tooltipContent}
      </ToggleTip>
    </Box>
  );
};

/**
 * Every width and offset here is a percentage of the chart's own box rather than a measured pixel
 * count, so the browser lays the bar out at whatever width its row has. A measured chart prints at
 * the width the screen last gave it: printing lays the page out again and takes the snapshot
 * before anything gets the chance to measure.
 *
 * The chart is its own positioning context, so the hover layer and the marker are laid over the
 * bar they belong to rather than over whatever positioned ancestor the page happens to have.
 */
const BarStackChart = ({
  height = 20,
  items,
  cardBottomView = false,
  markerValue = 0,
}: BarStackChartProps) => {
  const segmentSx = segmentStyles(height, cardBottomView);
  const { total, minIndex, maxIndex } = segmentBounds(items);
  const itemSx = (index: number) =>
    pickSegmentStyle(segmentSx, index, minIndex, maxIndex);

  const markerOffset = calculateMarkerOffset(markerValue);

  return (
    <Box sx={{ position: "relative" }}>
      <Box sx={{ ...OVERLAY_STYLE, pt: 3, zIndex: 2 }}>
        {items.map((item: BarItem, index) => (
          <HoverItem
            key={index}
            width={calculateWidth(item.value, total)}
            sx={itemSx(index)}
            tooltipContent={item.content}
          ></HoverItem>
        ))}
      </Box>
      {markerValue ? (
        <Box sx={{ ...OVERLAY_STYLE, left: markerOffset }}>
          <svg width={1} height={height + 10} overflow="visible">
            <LineMarker position={0}></LineMarker>
          </svg>
        </Box>
      ) : (
        <></>
      )}
      <Box sx={{ pt: 3 }}>
        <Box
          sx={{
            display: "flex",
            "flex-direction": "row",
            "justify-content": "flex-start",
          }}
        >
          {items.map((item: BarItem, index) => (
            <Box
              key={index}
              sx={{
                ...itemSx(index),
                "background-color": item.color,
                width: calculateWidth(item.value, total),
              }}
            />
          ))}
        </Box>
      </Box>
      {/* One decimal always, so a whole-number mean reads "3.0" beside a "2.6" rather than "3". Only
          beside a marker, so a chart with no mean drawn is not labelled "0.0". */}
      {markerValue ? (
        <Box sx={{ ml: `calc(${markerOffset} - 10px)` }}>
          {markerValue.toFixed(1)}
        </Box>
      ) : null}
    </Box>
  );
};

/** A layer pinned to the top of the chart, as wide as the bar under it. */
const OVERLAY_STYLE = {
  display: "flex",
  left: 0,
  position: "absolute",
  right: 0,
  top: 0,
} as const;

type SegmentStyles = ReturnType<typeof segmentStyles>;

/** The middle segments are square; the ends are rounded on the side that ends the bar. */
const segmentStyles = (height: number, cardBottomView: boolean) => {
  const sx = {
    height: `${height}px`,
    "text-align": "center",
  };

  return {
    middle: sx,
    left: cardBottomView
      ? { ...sx, "border-radius": "0 0 0 6px" }
      : { ...sx, "border-radius": "5px 0 0 5px" },
    right: cardBottomView
      ? { ...sx, "border-radius": "0 0 6px 0" }
      : { ...sx, "border-radius": "0 5px 5px 0" },
    single: cardBottomView
      ? { ...sx, "border-radius": "0 0 6px 6px" }
      : { ...sx, "border-radius": "5px 5px 5px 5px" },
  };
};

/** The total, and the first and last segments with anything in them — the two that get rounded. */
const segmentBounds = (items: BarItem[]) =>
  items.reduce(
    (accumulator, currentValue, currentIndex) => {
      return {
        total: accumulator.total + currentValue.value,
        minIndex:
          accumulator.minIndex === undefined && currentValue.value > 0
            ? currentIndex
            : accumulator.minIndex,
        maxIndex: currentValue.value > 0 ? currentIndex : accumulator.maxIndex,
      };
    },
    { total: 0, minIndex: undefined as number | undefined, maxIndex: 0 },
  );

const pickSegmentStyle = (
  styles: SegmentStyles,
  index: number,
  minIndex: number | undefined,
  maxIndex: number,
) => {
  if (index === minIndex && maxIndex === minIndex) return styles.single;
  if (index === maxIndex) return styles.right;
  if (index === minIndex) return styles.left;
  return styles.middle;
};

export default BarStackChart;
